// src/pages/TermsConditions.jsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const TermsContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background.primary};
  padding: 8rem 0 4rem;
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const PageHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  background: ${({ theme }) => theme.gradients.primary};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.5rem;
  }
`;

const LastUpdated = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1rem;
`;

const Content = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 20px;
  padding: 3rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  backdrop-filter: blur(10px);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 2rem;
  }
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
  
  h2 {
    color: ${({ theme }) => theme.colors.primary.yellow};
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1rem;
    border-bottom: 2px solid ${({ theme }) => theme.colors.primary.yellow};
    padding-bottom: 0.5rem;
  }
  
  h3 {
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: 1.2rem;
    font-weight: 600;
    margin: 1.5rem 0 0.75rem;
  }
  
  p {
    color: ${({ theme }) => theme.colors.text.secondary};
    line-height: 1.7;
    margin-bottom: 1rem;
  }
  
  ul, ol {
    color: ${({ theme }) => theme.colors.text.secondary};
    margin: 1rem 0;
    padding-left: 2rem;
    
    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }
  
  strong {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const HighlightBox = styled.div`
  background: ${({ theme }) => theme.gradients.cyber};
  border-radius: 15px;
  padding: 2rem;
  margin: 2rem 0;
  border: 1px solid ${({ theme }) => theme.colors.primary.purple};
  
  h3 {
    color: ${({ theme }) => theme.colors.primary.yellow};
    margin-bottom: 1rem;
  }
  
  p {
    color: ${({ theme }) => theme.colors.text.primary};
    margin-bottom: 0.5rem;
  }
  
  ul {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const ContactBox = styled.div`
  background: ${({ theme }) => theme.gradients.cyber};
  border-radius: 15px;
  padding: 2rem;
  margin-top: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.primary.purple};
  
  h3 {
    color: ${({ theme }) => theme.colors.primary.yellow};
    margin-bottom: 1rem;
  }
  
  p {
    color: ${({ theme }) => theme.colors.text.primary};
    margin-bottom: 0.5rem;
  }
  
  a {
    color: ${({ theme }) => theme.colors.primary.yellow};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const TermsConditions = () => {
  return (
    <TermsContainer>
      <ContentWrapper>
        <PageHeader
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <PageTitle>Terms & Conditions</PageTitle>
          <LastUpdated>Last Updated: January 2025</LastUpdated>
        </PageHeader>

        <Content
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Section>
            <h2>1. Acceptance of Terms</h2>
            <p>
              By registering for, attending, or participating in Security BSides Albuquerque 2025 
              ("the Conference"), you agree to be bound by these Terms and Conditions 
              ("Terms"). If you do not agree to these Terms, please do not register for 
              or attend the Conference.
            </p>
            <p>
              Security BSides Albuquerque is a community-driven, non-profit cybersecurity conference 
              organized by volunteers and held in Albuquerque, New Mexico, on July 25-26, 2025.
            </p>
          </Section>

          <Section>
            <h2>2. Conference Registration</h2>
            
            <h3>Registration Process</h3>
            <ul>
              <li>Registration is required for all attendees</li>
              <li>Registration fees range from $0 to $23.18 depending on the ticket type</li>
              <li>All registrations are subject to availability</li>
              <li>We reserve the right to limit registration numbers for safety and capacity reasons</li>
            </ul>

            <h3>Payment and Refunds</h3>
            <ul>
              <li>Payment is processed through secure third-party providers (Eventbrite)</li>
              <li>All fees are non-refundable unless the Conference is cancelled by the organizers</li>
              <li>In case of Conference cancellation, full refunds will be provided</li>
              <li>Registration transfers to other individuals are not permitted</li>
            </ul>

            <h3>Ticket Types</h3>
            <ul>
              <li><strong>Free Registration:</strong> Limited availability, first-come basis</li>
              <li><strong>Standard Registration:</strong> $23.18 including processing fees</li>
              <li><strong>Student Registration:</strong> Discounted rates with valid student ID</li>
            </ul>
          </Section>

          <Section>
            <h2>3. Code of Conduct</h2>
            
            <HighlightBox>
              <h3>Our Commitment</h3>
              <p>
                Security BSides Albuquerque is dedicated to providing a harassment-free conference 
                experience for everyone, regardless of gender, gender identity and expression, 
                age, sexual orientation, disability, physical appearance, body size, race, 
                ethnicity, religion, or technology choices.
              </p>
            </HighlightBox>

            <h3>Expected Behavior</h3>
            <ul>
              <li>Treat all participants with respect and courtesy</li>
              <li>Use welcoming and inclusive language</li>
              <li>Respect differing viewpoints and experiences</li>
              <li>Accept constructive criticism gracefully</li>
              <li>Focus on what is best for the community</li>
              <li>Show empathy towards other community members</li>
            </ul>

            <h3>Unacceptable Behavior</h3>
            <ul>
              <li>Harassment, intimidation, or discrimination of any kind</li>
              <li>Offensive verbal or written comments</li>
              <li>Inappropriate use of nudity, sexual images, or sexual language</li>
              <li>Deliberate intimidation, stalking, or following</li>
              <li>Sustained disruption of talks or other events</li>
              <li>Illegal or unethical activities</li>
            </ul>

            <h3>Enforcement</h3>
            <p>
              Violations of the Code of Conduct may result in immediate removal from the 
              Conference without refund and potential banning from future events.
            </p>
          </Section>

          <Section>
            <h2>4. Conference Content and Materials</h2>
            
            <h3>Presentations and Talks</h3>
            <ul>
              <li>All presentation content is the intellectual property of the respective speakers</li>
              <li>Recording of sessions may be permitted with speaker consent</li>
              <li>Attendees may take notes for personal use</li>
              <li>Commercial use of conference content is prohibited without permission</li>
            </ul>

            <h3>Photography and Recording</h3>
            <ul>
              <li>The Conference may be photographed and recorded for promotional purposes</li>
              <li>By attending, you consent to your likeness being used in conference materials</li>
              <li>If you object to being photographed, please notify organizers</li>
              <li>Live streaming of selected sessions may occur</li>
            </ul>
          </Section>

          <Section>
            <h2>5. Health and Safety</h2>
            
            <h3>Venue Safety</h3>
            <ul>
              <li>Follow all venue rules and emergency procedures</li>
              <li>Report safety concerns to conference staff immediately</li>
              <li>Comply with all local, state, and federal regulations</li>
              <li>Security measures may include bag checks</li>
            </ul>

            <h3>Health Considerations</h3>
            <ul>
              <li>Attend only if you are feeling well</li>
              <li>Inform organizers of any accessibility needs during registration</li>
              <li>Follow any health guidelines in effect at the time of the Conference</li>
              <li>Medical emergencies will be handled by professional medical personnel</li>
            </ul>

            <h3>Prohibited Items</h3>
            <ul>
              <li>Weapons of any kind</li>
              <li>Illegal substances</li>
              <li>Items that may pose a security threat</li>
              <li>Professional recording equipment (without prior authorization)</li>
            </ul>
          </Section>

          <Section>
            <h2>6. Liability and Disclaimers</h2>
            
            <h3>Limitation of Liability</h3>
            <p>
              Security BSides Albuquerque organizers, volunteers, sponsors, and venue providers shall 
              not be liable for any direct, indirect, incidental, special, or consequential 
              damages arising from your attendance at the Conference.
            </p>

            <h3>Personal Property</h3>
            <ul>
              <li>You are responsible for your personal belongings</li>
              <li>The Conference is not responsible for lost, stolen, or damaged items</li>
              <li>Secure storage facilities may not be available</li>
            </ul>

            <h3>Travel and Accommodation</h3>
            <ul>
              <li>Attendees are responsible for their own travel arrangements</li>
              <li>The Conference is not liable for travel delays or cancellations</li>
              <li>Hotel and accommodation bookings are the attendee's responsibility</li>
            </ul>
          </Section>

          <Section>
            <h2>7. Sponsorship and Vendor Relations</h2>
            <ul>
              <li>Sponsor contact information may be shared with explicit consent</li>
              <li>Vendors must comply with conference rules and guidelines</li>
              <li>Commercial solicitation outside designated areas is prohibited</li>
              <li>Business cards and networking materials are allowed in designated areas</li>
            </ul>
          </Section>

          <Section>
            <h2>8. Changes and Cancellation</h2>
            
            <h3>Conference Changes</h3>
            <ul>
              <li>Schedule, speakers, and content are subject to change</li>
              <li>Significant changes will be communicated to registered attendees</li>
              <li>No refunds will be provided for minor schedule changes</li>
            </ul>

            <h3>Force Majeure</h3>
            <p>
              The Conference may be cancelled or postponed due to circumstances beyond our 
              control, including but not limited to natural disasters, government regulations, 
              or public health emergencies. In such cases, we will work to reschedule or 
              provide appropriate refunds.
            </p>
          </Section>

          <Section>
            <h2>9. Intellectual Property</h2>
            <ul>
              <li>Security BSides Albuquerque name and logos are protected trademarks</li>
              <li>Speaker presentations remain the property of their creators</li>
              <li>Conference materials may not be reproduced without permission</li>
              <li>Respect all intellectual property rights of speakers and sponsors</li>
            </ul>
          </Section>

          <Section>
            <h2>10. Privacy and Data Protection</h2>
            <p>
              Your privacy is important to us. Please review our Privacy Policy for 
              information about how we collect, use, and protect your personal information. 
              By registering for the Conference, you consent to our data practices as 
              described in the Privacy Policy.
            </p>
          </Section>

          <Section>
            <h2>11. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the State of New Mexico, United States. 
              Any disputes arising from these Terms or your attendance at the Conference 
              shall be resolved in the courts of New Mexico.
            </p>
          </Section>

          <Section>
            <h2>12. Modifications</h2>
            <p>
              We reserve the right to modify these Terms at any time. Material changes 
              will be communicated to registered attendees. Continued participation in 
              the Conference after notification constitutes acceptance of the modified Terms.
            </p>
          </Section>

          <ContactBox>
            <h3>Contact Information</h3>
            <p>
              If you have any questions about these Terms & Conditions, please contact us:
            </p>
            <p>
              <strong>Email:</strong> <a href="mailto:info@bsidesabq.org">info@bsidesabq.org</a><br />
              <strong>Legal Questions:</strong> <a href="mailto:legal@bsidesabq.org">legal@bsidesabq.org</a><br />
              <strong>Website:</strong> <a href="https://bsidesabq.org">https://bsidesabq.org</a>
            </p>
            <p>
              <strong>Security BSides Albuquerque</strong><br />
              Albuquerque, New Mexico, USA<br />
              Conference Date: July 25-26, 2025
            </p>
          </ContactBox>
        </Content>
      </ContentWrapper>
    </TermsContainer>
  );
};

export default TermsConditions;